import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import clsx from 'clsx';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import {
  FormControl,
  Theme,
  MenuItem,
  Grid,
  Button,
  TextField,
  InputAdornment,
  Divider,
  IconButton,
  Typography,
  Slider
} from '@mui/material';
import { FilterList, Close, ArrowDropDown, ArrowDropUp, Search } from '@mui/icons-material';
import { IDateRange, IDropdownResponse } from '../../../models';
import { DateRangePicker } from '../../../components';
import { getVendorTemplates } from '../../../fetch';

interface IInProgressContractFilterProps {
  isLoading: boolean;
  applyFilters: (clearFilters?: boolean) => void;
  setVendorSearchValue: (val: string) => void;
  vendorSearchValue: string;
  handleVendorSearch: (clearSearch?: boolean) => void;
  setSelectedContractType: (val: string) => void;
  selectedContractType: string;
  setContractSearchValue: (val: string) => void;
  contractSearchValue: string;
  handleContractSearch: (clearSearch?: boolean) => void;
  selectedDateStartedRange: IDateRange | null;
  setSelectedDateStartedRange: Dispatch<SetStateAction<IDateRange>>;
  selectedContractAge?: number[];
  setSelectedContractAge?: Dispatch<SetStateAction<number[]>>;
  selectedLastChangedRange: IDateRange | null;
  setSelectedLastChangedRange: Dispatch<SetStateAction<IDateRange>>;
  maxContractAge: number;
  vendorSageId: string | number;
}

export const InProgressContractFilters: FC<IInProgressContractFilterProps> = ({
  isLoading,
  applyFilters,
  setVendorSearchValue,
  handleVendorSearch,
  vendorSearchValue,
  setSelectedContractType,
  selectedContractType,
  setContractSearchValue,
  contractSearchValue,
  handleContractSearch,
  selectedDateStartedRange,
  setSelectedDateStartedRange,
  selectedContractAge,
  setSelectedContractAge,
  selectedLastChangedRange,
  setSelectedLastChangedRange,
  maxContractAge = 365,
  vendorSageId
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [isMobileFilterButtonOpen, toggleMobileFilter] = useState<boolean>(false);

  const classes = useStyles({ isMobileFilterButtonOpen });

  const [isLoadingContractTypes, setIsLoadingContractTypes] = useState<boolean>(false);
  const [contractTypes, setContractTypes] = useState<IDropdownResponse[]>([]);
  const fetchContractTypes = async () => {
    try {
      setIsLoadingContractTypes(true);
      const res = await getVendorTemplates({ hasInprogressDocuments: true });
      const data = res.records.map(option => ({
        value: option.name,
        description: option.name,
        shorthand: option.pandaDocTemplateId
      }));
      setContractTypes(data);
    } catch (error) {
      enqueueSnackbar(`Error loading contract types, please try again.`, {
        variant: 'error'
      });
    } finally {
      setIsLoadingContractTypes(false);
    }
  };

  // Used to help determine when to toggle hasAppliedFilters on/off for search specific functions
  const hasSelectedFilters = () => {
    return (
      !!vendorSearchValue ||
      !!selectedContractType ||
      !!contractSearchValue ||
      !!selectedDateStartedRange?.startDate ||
      !!selectedDateStartedRange?.endDate ||
      selectedContractAge[0] > 0 ||
      selectedContractAge[1] > 0 ||
      !!selectedLastChangedRange?.startDate ||
      !!selectedLastChangedRange?.endDate
    );
  };

  useEffect(() => {
    fetchContractTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Button
        color="secondary"
        variant="contained"
        className={classes.mobileButton}
        startIcon={<FilterList />}
        endIcon={isMobileFilterButtonOpen ? <ArrowDropUp /> : <ArrowDropDown />}
        onClick={() => {
          toggleMobileFilter(!isMobileFilterButtonOpen);
        }}
      >
        Filters
      </Button>
      {!isMobileFilterButtonOpen && <Divider className={classes.divider} />}
      <Grid container spacing={1} alignItems="center" className={classes.wrapper}>
        {/* Search can't work with filter by vendorSageId, so hide the field */}
        {!vendorSageId && (
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              placeholder="Search Vendor..."
              name="vendorSearch"
              value={vendorSearchValue}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                    className={classes.searchIcon}
                    onClick={() => {
                      if (vendorSearchValue.length > 0) {
                        setHasAppliedFilters(true);
                        handleVendorSearch();
                      } else if (!hasSelectedFilters()) {
                        setHasAppliedFilters(false);
                      }
                    }}
                  >
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: vendorSearchValue ? (
                  <InputAdornment
                    position="end"
                    className={classes.searchIcon}
                    onClick={() => {
                      setVendorSearchValue('');
                      handleVendorSearch(true);
                      if (!contractSearchValue) {
                        setHasAppliedFilters(false);
                      }
                    }}
                  >
                    <Close />
                  </InputAdornment>
                ) : null
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' && vendorSearchValue.length > 0) {
                  setHasAppliedFilters(true);
                  handleVendorSearch();
                }
              }}
              onChange={e => {
                setVendorSearchValue(e.target.value);
              }}
            />
          </Grid>
        )}

        <Grid item xs={12} sm={6} md={4} lg={3}>
          <TextField
            select
            size="small"
            fullWidth
            variant="outlined"
            name="contractType"
            label={isLoadingContractTypes ? 'Loading...' : 'Contract Type'}
            value={selectedContractType}
            disabled={isLoading || isLoadingContractTypes}
            InputProps={{
              endAdornment: selectedContractType ? (
                <InputAdornment
                  position="end"
                  sx={{
                    position: 'absolute',
                    right: 27,
                    cursor: 'pointer'
                  }}
                >
                  <IconButton
                    component="span"
                    title="Clear"
                    size="small"
                    onClick={() => {
                      setSelectedContractType('');
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
            onChange={e => {
              setSelectedContractType(e.target.value as string);
            }}
          >
            {contractTypes &&
              contractTypes.map((type, index) => {
                return (
                  <MenuItem key={`${type.value}-${index}`} value={type.value}>
                    {type.description}
                  </MenuItem>
                );
              })}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <TextField
            size="small"
            fullWidth
            variant="outlined"
            placeholder="Search Name..."
            name="contractNameSearch"
            value={contractSearchValue}
            disabled={isLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment
                  position="start"
                  className={classes.searchIcon}
                  onClick={() => {
                    if (contractSearchValue.length > 0) {
                      setHasAppliedFilters(true);
                      handleContractSearch();
                    } else if (!hasSelectedFilters()) {
                      setHasAppliedFilters(false);
                    }
                  }}
                >
                  <Search />
                </InputAdornment>
              ),
              endAdornment: contractSearchValue ? (
                <InputAdornment
                  position="end"
                  className={classes.searchIcon}
                  onClick={() => {
                    setContractSearchValue('');
                    handleContractSearch(true);
                    if (!hasSelectedFilters()) {
                      setHasAppliedFilters(false);
                    }
                  }}
                >
                  <Close />
                </InputAdornment>
              ) : null
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' && contractSearchValue.length > 0) {
                setHasAppliedFilters(true);
                handleContractSearch();
              }
            }}
            onChange={e => {
              setContractSearchValue(e.target.value);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <FormControl fullWidth variant="outlined" size="small">
            <DateRangePicker
              id="dateStartedRange"
              label="Date Started"
              value={selectedDateStartedRange}
              isDisabled={isLoading}
              onChange={value => {
                setSelectedDateStartedRange(value);
              }}
              fullWidth
              inputSize="small"
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Typography>Contract Age</Typography>
          <Slider
            value={selectedContractAge}
            onChange={(_, val) => {
              setSelectedContractAge(val as number[]);
            }}
            valueLabelDisplay="auto"
            max={maxContractAge}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <FormControl fullWidth variant="outlined" size="small">
            <DateRangePicker
              id="lastChangedRange"
              label="Last Changed Date"
              value={selectedLastChangedRange}
              isDisabled={isLoading}
              onChange={value => {
                setSelectedLastChangedRange(value);
              }}
              fullWidth
              inputSize="small"
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={5}>
          <Button
            color="primary"
            variant="contained"
            className={classes.button}
            disabled={isLoading || isLoadingContractTypes}
            startIcon={<FilterList />}
            onClick={() => {
              if (
                vendorSearchValue.length > 0 ||
                contractSearchValue.length > 0 ||
                hasSelectedFilters()
              ) {
                setHasAppliedFilters(true);
              } else {
                setHasAppliedFilters(false);
              }
              applyFilters();
            }}
          >
            Apply Filters
          </Button>
          {hasAppliedFilters && (
            <Button
              color="inherit"
              className={clsx(classes.button, classes.resetButton)}
              variant="contained"
              disabled={isLoading}
              startIcon={<Close />}
              onClick={() => {
                handleVendorSearch(true);
                handleContractSearch(true);
                setHasAppliedFilters(false);
                setVendorSearchValue('');
                setSelectedContractType('');
                setContractSearchValue('');
                setSelectedDateStartedRange(undefined);
                setSelectedContractAge([0, 0]);
                setSelectedLastChangedRange(undefined);
                applyFilters(true);
              }}
            >
              Reset
            </Button>
          )}
        </Grid>
      </Grid>
    </>
  );
};

const useStyles = makeStyles<Theme, { isMobileFilterButtonOpen?: boolean }>((theme: Theme) => ({
  mobileButton: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  },
  button: {
    height: 40,
    textTransform: 'capitalize',
    width: '100%',
    '@media (min-width: 400px)': {
      width: '48%'
    },
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  resetButton: {
    '@media (min-width: 400px)': {
      marginLeft: 11
    },
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1)
    }
  },
  wrapper: ({ isMobileFilterButtonOpen }) =>
    isMobileFilterButtonOpen
      ? {
          marginTop: 10,
          marginBottom: 10,
          display: 'flex'
        }
      : {
          display: 'none',
          marginBottom: theme.spacing(1),
          [theme.breakpoints.up('sm')]: {
            display: 'flex',
            marginBottom: theme.spacing(1)
          }
        },
  divider: {
    display: 'block',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  },
  gridItem: {
    padding: `3px 8px !important`,
    [theme.breakpoints.up('sm')]: {
      padding: `8px !important`
    }
  },
  searchIcon: {
    cursor: 'pointer',
    color: theme.palette.grey[500]
  },
  ellipsis: {
    fontSize: 14,
    width: 'auto',
    whiteSpace: 'normal',
    overflow: 'visible',
    '@media (min-width: 960px)': {
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden'
    },
    '@media (min-width: 1400px)': {
      width: 'auto',
      whiteSpace: 'normal',
      overflow: 'visible'
    }
  }
}));
